/**
 * Deferred toast facade.
 *
 * sonner — and its <Toaster/> — are only imported the first time a toast is
 * actually triggered, so they stay out of the initial bundle. The API mirrors
 * sonner's `toast` (including .success/.error/…), it just resolves a moment
 * later.
 */
let activateHost = () => {};
let hostRegistered = false;
let activationPending = false;
let resolveReady;
const hostReady = new Promise((resolve) => { resolveReady = resolve; });

/** Called by the toast host so it can mount its own <Toaster/> on demand. */
export function registerToastHost(fn) {
  activateHost = fn;
  hostRegistered = true;
  // A toast fired during the first render happens before the host registers.
  if (activationPending) fn();
}

function requestHost() {
  hostRegistered ? activateHost() : (activationPending = true);
}

/** Called by the toast host once sonner's subscription is live. */
export function toastHostReady() {
  resolveReady();
}

async function fire(method, args) {
  requestHost();
  await hostReady;
  const { toast: sonnerToast } = await import('sonner');
  return method ? sonnerToast[method](...args) : sonnerToast(...args);
}

export const toast = (...args) => fire(null, args);

['success', 'error', 'info', 'warning', 'loading', 'promise', 'custom', 'message', 'dismiss']
  .forEach((method) => {
    toast[method] = (...args) => fire(method, args);
  });